import { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { useAuth } from '../context/AuthContext';
import { commissionsAPI, barbersAPI } from '../lib/api';
import { formatCurrency, formatDate, getStartOfMonth, getEndOfMonth, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Receipt, 
  User,
  Check,
  Loader2,
  DollarSign,
  TrendingUp,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import { exportToExcel, exportToPDF } from '../lib/exportUtils';
import { toast } from 'sonner';

export default function ComisionesPage() {
  const { selectedBusiness } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, paid: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedBarber, setSelectedBarber] = useState('all');
  const [showPaid, setShowPaid] = useState(false);
  const [startDate, setStartDate] = useState(getStartOfMonth());
  const [endDate, setEndDate] = useState(getEndOfMonth());
  const [selectedCommissions, setSelectedCommissions] = useState([]);

  const loadData = useCallback(async () => {
    if (!selectedBusiness) return;
    
    try {
      setLoading(true);
      const [commissionsRes, barbersRes] = await Promise.all([
        commissionsAPI.getAll({
          barber_id: selectedBarber !== 'all' ? selectedBarber : undefined,
          is_paid: showPaid ? true : undefined,
          start_date: startDate,
          end_date: endDate
        }),
        barbersAPI.getAll({ include_pos_shared: true })
      ]);
      
      setCommissions(commissionsRes.data.commissions);
      setStats({
        total: commissionsRes.data.total,
        pending: commissionsRes.data.pending,
        paid: commissionsRes.data.paid
      });
      setBarbers(barbersRes.data);
    } catch (error) {
      logger.error('Error loading commissions:', error);
      toast.error('Error al cargar comisiones');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness, selectedBarber, showPaid, startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getBarber = (barberId) => barbers.find(b => b.id === barberId);

  const toggleCommission = (commissionId) => {
    if (selectedCommissions.includes(commissionId)) {
      setSelectedCommissions(selectedCommissions.filter(id => id !== commissionId));
    } else {
      setSelectedCommissions([...selectedCommissions, commissionId]);
    }
  };

  const selectAllPending = () => {
    const pendingIds = commissions.filter(c => !c.is_paid).map(c => c.id);
    setSelectedCommissions(pendingIds);
  };

  const handlePayCommissions = async () => {
    if (selectedCommissions.length === 0) {
      toast.error('Selecciona al menos una comisión');
      return;
    }

    // Get barber from first selected commission
    const firstCommission = commissions.find(c => c.id === selectedCommissions[0]);
    if (!firstCommission) return;

    try {
      await commissionsAPI.pay(firstCommission.barber_id, selectedCommissions);
      toast.success('Comisiones marcadas como pagadas');
      setSelectedCommissions([]);
      loadData();
    } catch (error) {
      toast.error('Error al procesar pago');
    }
  };

  // Group commissions by barber
  const commissionsByBarber = commissions.reduce((acc, c) => {
    if (!acc[c.barber_id]) {
      acc[c.barber_id] = {
        barber: getBarber(c.barber_id),
        commissions: [],
        total: 0,
        pending: 0
      };
    }
    acc[c.barber_id].commissions.push(c);
    acc[c.barber_id].total += c.commission_amount;
    if (!c.is_paid) acc[c.barber_id].pending += c.commission_amount;
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-fade-in" data-testid="comisiones-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Comisiones
          </h1>
          <p className="text-slate-500 mt-1">
            Gestión de comisiones de {selectedBusiness?.name}
          </p>
        </div>
        {selectedCommissions.length > 0 && (
          <Button 
            onClick={handlePayCommissions}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Check className="w-4 h-4 mr-2" />
            Marcar como pagadas ({selectedCommissions.length})
          </Button>
        )}
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            onClick={() => {
              const rows = commissions.map((c) => ({
                fecha: c.created_at?.split('T')[0] || '',
                barbero: getBarber(c.barber_id)?.display_name || '—',
                concepto: c.sale_item_name || '',
                venta: Number(c.sale_amount || 0).toFixed(2),
                comision: Number(c.commission_amount || 0).toFixed(2),
                estado: c.is_paid ? 'Pagada' : 'Pendiente',
              }));
              exportToExcel(`Comisiones_${startDate}_a_${endDate}`, [{
                name: 'Comisiones',
                columns: [
                  { header: 'Fecha', key: 'fecha' },
                  { header: 'Barbero', key: 'barbero' },
                  { header: 'Concepto', key: 'concepto' },
                  { header: 'Venta', key: 'venta' },
                  { header: 'Comisión', key: 'comision' },
                  { header: 'Estado', key: 'estado' },
                ],
                rows,
              }]);
              toast.success('Excel descargado');
            }}
            data-testid="export-commissions-excel"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button
            variant="outline"
            className="border-red-200 text-red-700 hover:bg-red-50"
            onClick={() => {
              const rows = commissions.map((c) => ({
                fecha: c.created_at?.split('T')[0] || '',
                barbero: getBarber(c.barber_id)?.display_name || '—',
                concepto: c.sale_item_name || '',
                comision: Number(c.commission_amount || 0).toFixed(2),
                estado: c.is_paid ? 'Pagada' : 'Pendiente',
              }));
              exportToPDF(`Comisiones_${startDate}_a_${endDate}`, {
                businessName: selectedBusiness?.name,
                title: 'Reporte de comisiones',
                subtitle: `Período: ${startDate} → ${endDate}`,
                sections: [{
                  title: 'Detalle',
                  summary: [
                    `Total: ${formatCurrency(stats.total)}`,
                    `Pendiente: ${formatCurrency(stats.pending)}`,
                    `Pagadas: ${formatCurrency(stats.paid)}`,
                  ],
                  columns: [
                    { header: 'Fecha', key: 'fecha' },
                    { header: 'Barbero', key: 'barbero' },
                    { header: 'Concepto', key: 'concepto' },
                    { header: 'Comisión', key: 'comision' },
                    { header: 'Estado', key: 'estado' },
                  ],
                  rows,
                }],
              });
              toast.success('PDF descargado');
            }}
            data-testid="export-commissions-pdf"
          >
            <FileText className="w-4 h-4 mr-2" /> PDF
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200 bg-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-slate-100">
                <DollarSign className="w-6 h-6 text-slate-500" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total comisiones</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  {formatCurrency(stats.total)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-500/30 bg-amber-500/10">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-amber-500/20">
                <TrendingUp className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-amber-400/80">Por pagar</p>
                <p className="text-2xl font-bold text-amber-400" style={{ fontFamily: 'Manrope' }}>
                  {formatCurrency(stats.pending)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-500/30 bg-emerald-500/10">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-emerald-500/20">
                <Check className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-emerald-400/80">Pagadas</p>
                <p className="text-2xl font-bold text-emerald-400" style={{ fontFamily: 'Manrope' }}>
                  {formatCurrency(stats.paid)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 bg-white">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-slate-500">Barbero</Label>
              <Select value={selectedBarber} onValueChange={setSelectedBarber}>
                <SelectTrigger className="w-48 bg-slate-100 border-slate-200">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="all">Todos</SelectItem>
                  {barbers.map(barber => (
                    <SelectItem key={barber.id} value={barber.id}>
                      {barber.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-slate-500">Desde</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40 bg-slate-100 border-slate-200"
              />
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-slate-500">Hasta</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40 bg-slate-100 border-slate-200"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer ml-auto">
              <Checkbox 
                checked={showPaid} 
                onCheckedChange={setShowPaid}
                className="border-slate-300"
              />
              Solo pagadas
            </label>

            <Button variant="outline" size="sm" onClick={selectAllPending} className="border-slate-200">
              Seleccionar pendientes
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Commissions by Barber */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
        </div>
      ) : Object.keys(commissionsByBarber).length === 0 ? (
        <Card className="border-slate-200 bg-white">
          <CardContent className="flex flex-col items-center justify-center h-64 text-slate-500">
            <Receipt className="w-12 h-12 mb-3 opacity-50" />
            <p>No hay comisiones en este período</p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(commissionsByBarber).map(([barberId, data]) => (
          <Card key={barberId} className="border-slate-200 bg-white">
            <CardHeader className="border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-slate-200 text-white">
                      {data.barber?.display_name?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                      {data.barber?.display_name || 'Barbero desconocido'}
                    </CardTitle>
                    <p className="text-sm text-slate-500">
                      {data.commissions.length} comisiones
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Total</p>
                  <p className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                    {formatCurrency(data.total)}
                  </p>
                  {data.pending > 0 && (
                    <p className="text-sm text-amber-400">
                      {formatCurrency(data.pending)} pendiente
                    </p>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Servicio/Producto</TableHead>
                    <TableHead>Venta</TableHead>
                    <TableHead>Comisión</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.commissions.map((commission, index) => (
                    <TableRow 
                      key={commission.id}
                      className={cn(
                        "animate-fade-in",
                        selectedCommissions.includes(commission.id) && "bg-white/5"
                      )}
                      style={{ animationDelay: `${index * 0.03}s` }}
                    >
                      <TableCell>
                        {!commission.is_paid && (
                          <Checkbox
                            checked={selectedCommissions.includes(commission.id)}
                            onCheckedChange={() => toggleCommission(commission.id)}
                            className="border-slate-300"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-500">{formatDate(commission.created_at)}</span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{commission.sale_item_name}</p>
                          <p className="text-xs text-slate-500 capitalize">{commission.sale_item_type}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-500">{formatCurrency(commission.sale_amount)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="font-bold">{formatCurrency(commission.commission_amount)}</span>
                        <span className="text-xs text-slate-500 ml-1">
                          ({commission.commission_rate}% + ${commission.commission_fixed})
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={cn(
                            commission.is_paid 
                              ? "text-emerald-400 border-emerald-500/30" 
                              : "text-amber-400 border-amber-500/30"
                          )}
                        >
                          {commission.is_paid ? 'Pagada' : 'Pendiente'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
